import type { ParsedSunoOutput } from '@/types';
import { normalizeTitle } from '@/shared/titleNormalize';

export interface UniqueTitleDeps {
  /** Every title already in the user's Vault. */
  fetchTitles: () => Promise<string[]>;
  /** One plain-text model call on the user's active provider. */
  askModel: (prompt: string) => Promise<string>;
}

async function defaultFetchTitles(): Promise<string[]> {
  const { API_ENDPOINTS } = await import('@/config/api');
  const res = await fetch(API_ENDPOINTS.drafts.titles, { credentials: 'include' });
  if (!res.ok) throw new Error(`titles HTTP ${res.status}`);
  return ((await res.json()) as { titles?: string[] }).titles ?? [];
}

async function defaultAskModel(prompt: string): Promise<string> {
  const { generateSingleField } = await import('@/services/narrativeService');
  return generateSingleField(prompt);
}

/**
 * RiffWriter's half of the duplicate-title guard (Studio Team / Album / bulk
 * run the server-side enforceUniqueTitle). A generated title that is already in
 * the Vault gets one replacement drawn from the song's own hook, retried once.
 * Only title/titleArtist change -- the lyrics may sing the old title as their
 * hook. Never throws: a failed check leaves the result as generated.
 */
export async function ensureUniqueSongTitle(
  result: ParsedSunoOutput,
  deps: UniqueTitleDeps = { fetchTitles: defaultFetchTitles, askModel: defaultAskModel },
): Promise<{ result: ParsedSunoOutput; changedFrom: string | null }> {
  try {
    const original = (result.title || '').trim();
    if (!original) return { result, changedFrom: null };
    const taken = new Set((await deps.fetchTitles()).map(normalizeTitle));
    if (!taken.has(normalizeTitle(original))) return { result, changedFrom: null };

    const tried = [original];
    for (let attempt = 0; attempt < 2; attempt++) {
      const reply = await deps.askModel(`The song title ${tried.map(t => `"${t}"`).join(' and ')} is already used by another song in this catalog. Give ONE replacement title for the song below: 2-5 words, taken from its own hook or strongest lyric image, not a generic phrase. Reply with the title only -- no quotes, no label, no explanation.

LYRICS:
${(result.lyricsAlone || result.lyricsWithTags || '').slice(0, 4000)}`);
      const cand = (reply.split('\n').map(l => l.trim()).find(Boolean) || '')
        .replace(/^(title|new title)\s*:\s*/i, '').replace(/^["“'*]+|["”'*.]+$/g, '').trim().slice(0, 80);
      if (!cand) continue;
      if (taken.has(normalizeTitle(cand))) { tried.push(cand); continue; }
      const titleArtist = result.titleArtist?.includes(original)
        ? result.titleArtist.replace(original, cand)
        : result.titleArtist;
      return { result: { ...result, title: cand, titleArtist }, changedFrom: original };
    }
    console.warn(`[riffwriter] title "${original}" is already in the Vault and no replacement was found`);
    return { result, changedFrom: null };
  } catch (err) {
    console.warn('[riffwriter] duplicate-title check skipped:', err instanceof Error ? err.message : err);
    return { result, changedFrom: null };
  }
}
